import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Clock, CheckCircle, BookMarked, Filter, Loader2 } from "lucide-react";
import { getTopicMetadata } from "@/lib/topicMetadata";
import { StudentEmptyState } from "@/components/StudentEmptyState";

interface ReadingMaterial {
  id: string;
  title: string;
  slug: string;
  grade: number;
  subject: string;
  topic: string;
  summary: string | null;
  coverImageUrl: string | null;
  estimatedReadTime: number;
  status: string;
  publishedAt: string | null;
}

interface ReadingProgress {
  materialId: string;
  status: 'not_started' | 'in_progress' | 'completed';
  scrollPosition: number;
  totalTimeSeconds: number;
  completedAt: string | null;
}

interface ReadingMaterialWithProgress extends ReadingMaterial {
  progress?: ReadingProgress;
}

interface Subject {
  id: string;
  name: string;
  displayName: string;
}

interface Topic {
  id: string;
  subjectId: string;
  name: string;
  displayName: string;
}

export default function ReadingLibrary() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [filterSubject, setFilterSubject] = useState<string>('all');
  const [filterTopic, setFilterTopic] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation('/login');
    }
  }, [isAuthenticated, authLoading, setLocation]);

  const { data: materials = [], isLoading: materialsLoading } = useQuery<ReadingMaterialWithProgress[]>({
    queryKey: ['/api/reading'],
    enabled: isAuthenticated,
  });

  const { data: subjects = [] } = useQuery<Subject[]>({
    queryKey: ['/api/subjects', user?.grade],
    enabled: isAuthenticated && !!user?.grade,
  });

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ['/api/topics'],
    enabled: isAuthenticated,
  });

  const { data: readingStats } = useQuery({
    queryKey: ['/api/reading/stats'],
    enabled: isAuthenticated,
  });

  const filteredMaterials = materials.filter((material) => {
    if (filterSubject !== 'all' && material.subject !== filterSubject) return false;
    if (filterTopic !== 'all' && material.topic !== filterTopic) return false;
    if (filterStatus !== 'all') {
      const progressStatus = material.progress?.status || 'not_started';
      if (filterStatus !== progressStatus) return false;
    }
    return true;
  });

  const completedCount = materials.filter(m => m.progress?.status === 'completed').length;
  const inProgressCount = materials.filter(m => m.progress?.status === 'in_progress').length;
  const totalCount = materials.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const getStatusBadge = (progress?: ReadingProgress) => {
    if (!progress || progress.status === 'not_started') {
      return <Badge variant="outline">Not Started</Badge>;
    }
    if (progress.status === 'completed') {
      return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
    }
    return <Badge variant="secondary"><BookMarked className="w-3 h-3 mr-1" /> In Progress</Badge>;
  };

  const formatReadTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    if (minutes < 1) return 'Less than 1 min';
    return `${minutes} min read`;
  };

  if (authLoading || materialsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Reading Library</h1>
        <p className="text-muted-foreground">
          Explore lessons and reading materials for your grade level
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="p-3 bg-primary/10 rounded-full">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalCount}</p>
              <p className="text-sm text-muted-foreground">Total Materials</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{completedCount}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <BookMarked className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{inProgressCount}</p>
              <p className="text-sm text-muted-foreground">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filter Materials
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={filterSubject} onValueChange={(value) => { setFilterSubject(value); setFilterTopic('all'); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjects.map((s) => (
                  <SelectItem key={s.id} value={s.name}>
                    {s.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterTopic} onValueChange={setFilterTopic}>
              <SelectTrigger>
                <SelectValue placeholder="All Topics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Topics</SelectItem>
                {topics
                  .filter(t => filterSubject === 'all' || subjects.find(s => s.name === filterSubject && s.id === t.subjectId))
                  .map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.displayName}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {filteredMaterials.length === 0 ? (
        <Card>
          <CardContent className="p-4 md:p-5">
            <StudentEmptyState
              illustration="owl-book"
              title="Nothing to read here yet"
              hint={totalCount === 0
                ? "No reading materials for your grade yet. Owlbert will let you know when they land."
                : "Try changing your filters to see more materials."}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMaterials.map((material) => {
            const metadata = getTopicMetadata(material.topic, material.subject);
            const Icon = metadata.icon;
            
            return (
              <Card 
                key={material.id} 
                className="hover-elevate cursor-pointer transition-all"
                onClick={() => setLocation(`/reading/${material.slug}`)}
              >
                {material.coverImageUrl && (
                  <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                    <img 
                      src={material.coverImageUrl} 
                      alt={material.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardHeader className={material.coverImageUrl ? 'pt-4' : ''}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <Badge variant="outline" className="capitalize">{material.subject}</Badge>
                    </div>
                    {getStatusBadge(material.progress)}
                  </div>
                  <CardTitle className="text-lg mt-3">{material.title}</CardTitle>
                  {material.summary && (
                    <CardDescription className="line-clamp-2">
                      {material.summary}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{material.estimatedReadTime} min read</span>
                    </div>
                    <Badge variant="outline" className="capitalize">{material.topic}</Badge>
                  </div>
                  {material.progress?.status === 'in_progress' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>Progress</span>
                        <span>{Math.round(material.progress.scrollPosition)}%</span>
                      </div>
                      <Progress value={material.progress.scrollPosition} className="h-1" />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
